import { lazy, Suspense } from 'react';
import { Loader } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// React.lazy dedupes the dynamic import, so defining these here as well as in
// the double-buffered PdfDocumentLayer is fine. The shared module configures
// the pdf.js worker before either component can mount a <Document>.
const Document = lazy(() => import('./react-pdf-lazy').then((mod) => ({ default: mod.Document })));
const Page = lazy(() => import('./react-pdf-lazy').then((mod) => ({ default: mod.Page })));

interface FinalizedPdfViewerProps {
  /** The finalized (already-signed) PDF URL from SigDocumentUrl. */
  fileUrl: string;
  numPages: number | null;
  scale: number;
  pageWidth: number;
  pageHeight: number;
  onLoadSuccess: (info: { numPages: number }) => void;
}

/**
 * Renders the finalized, already-signed PDF once the document is complete.
 * Unlike the editable preview this branch is a single non-buffered Document —
 * no hover/edit/signature overlays, just the signed pages.
 */
export function FinalizedPdfViewer({
  fileUrl,
  numPages,
  scale,
  pageWidth,
  pageHeight,
  onLoadSuccess,
}: FinalizedPdfViewerProps) {
  const { t } = useTranslation();

  return (
    <div className="max-w-4xl mx-auto">
      <Suspense
        fallback={
          <div className="flex items-center justify-center py-20">
            <Loader className="h-8 w-8 animate-spin text-primary" />
          </div>
        }
      >
        <Document
          file={fileUrl}
          onLoadSuccess={onLoadSuccess}
          loading={
            <div className="flex items-center justify-center py-20">
              <Loader className="h-8 w-8 animate-spin text-primary" />
            </div>
          }
          error={
            <div className="flex items-center justify-center py-20">
              <p className="text-destructive font-semibold">
                {t('GENERATEREVIEW_PREVIEW_FAILEDTOLOAD')}
              </p>
            </div>
          }
        >
          <div className="space-y-4">
            {numPages &&
              Array.from({ length: numPages }, (_, i) => i + 1).map((page) => (
                <div
                  key={page}
                  data-page-number={page}
                  className="bg-white shadow-xl mx-auto relative rounded-lg"
                  style={{ width: pageWidth * scale, height: pageHeight * scale }}
                >
                  <Page
                    pageNumber={page}
                    scale={scale}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    loading={
                      <div
                        className="flex items-center justify-center bg-[hsl(var(--bg))]"
                        style={{ width: pageWidth * scale, height: pageHeight * scale }}
                      >
                        <Loader className="h-8 w-8 animate-spin text-primary" />
                      </div>
                    }
                  />
                  {numPages && (
                    <div className="pointer-events-none absolute bottom-3 right-3 text-globe-grey text-sm font-semibold tracking-tight">
                      {t('GENERATEREVIEW_PAGEINDICATOR', { page, total: numPages })}
                    </div>
                  )}
                </div>
              ))}
          </div>
        </Document>
      </Suspense>
    </div>
  );
}
